/**
 * Constants of the upload watcher: how often it asks after the batches
 * it is waiting on, how long it is willing to wait, and where it keeps
 * that list across a reload.
 *
 * The watcher runs for the whole session rather than for one screen, so
 * every number here is spent on every page the user visits. They are
 * gathered in one place for that reason.
 */
export abstract class UploadWatcherConst {

    /**
     * How long to wait between one round of asking and the next.
     *
     * Counted from the end of a round to the start of the following one,
     * so a slow answer is never joined by a second request.
     *
     * The same five seconds the status list polls on, so a user with the
     * modal open and a user who has walked away learn of an outcome at
     * the same pace.
     */
    public static readonly POLL_INTERVAL_MS = 5_000;

    /**
     * How long a single batch may be watched before it is given up on.
     *
     * A batch the server never finishes would otherwise be asked after
     * for as long as the tab stays open. Fifteen minutes is far longer
     * than any batch the upload limits allow, so reaching it means the
     * batch is not coming back rather than that it is slow.
     */
    public static readonly MAX_WATCH_MS = 900_000;

    /**
     * How long a toast reporting a rejected file stays on screen.
     *
     * Longer than the default, because it can arrive on a page the user
     * is reading rather than on the one they uploaded from: it has to
     * outlast the moment of noticing it, and it is the only place the
     * rejection is announced.
     *
     * A batch that went through entirely keeps the default timeout —
     * there is nothing in it to act on.
     */
    public static readonly FAILURE_TIMEOUT_MS = 10_000;

    /**
     * How long a finished batch stays in the menu after it has been
     * announced.
     *
     * The completion and the full bar arrive in the same round, so a row
     * dropped on completion is a bar that jumps to its end and disappears
     * in one frame — the user is shown every part of the upload except
     * the part that says it worked.
     *
     * Four seconds is long enough to be caught by someone whose attention
     * is elsewhere on the page, and short enough that a menu they open
     * afterwards is about what is happening now. Nothing is asked of the
     * server during it: the batch stops being polled the moment it is
     * marked, and only the row is held.
     */
    public static readonly SETTLED_LINGER_MS = 4_000;

    /**
     * Where the watched batch ids are kept so a reload does not lose
     * them.
     *
     * Session storage and not local storage on purpose: the watch belongs
     * to the tab the upload was started in, and should not be inherited
     * by a tab opened tomorrow.
     */
    public static readonly STORAGE_KEY = 'sadin.uploads.watched';

}
